/**
 * Copyright IBM Corp. 2021
 *
 * This source code is licensed under the IBM Public License Version 1.0 found in the
 * LICENSE file in the root directory of this source tree.
 */
import Hammer from "hammerjs";
import throttle from "lodash/throttle";
import TriFloorPlanUtils from "../util/TriFloorPlanUtils.js";
import TriFloorPlanPlugin from "./TriFloorPlanPlugin.js";

export default class TriZoomPlugin extends TriFloorPlanPlugin {
  static type = "zoom";
  constructor() {
    super();
    this.hammer = null;
    this.pinchInfo = null;
  }

  get maxScale() {
    return this.props.maxScale != null ? this.props.maxScale : 10;
  }

  get minScale() {
    return this.props.minScale != null ? this.props.minScale : 0.25;
  }

  get doubleTapZoomFactor() {
    return this.props.doubleTapZoomFactor != null
      ? this.props.doubleTapZoomFactor
      : 2;
  }

  pluginDidUpdate(prevProps) {
    const { floorPlanLoaded, enabled } = this.props;
    if (!floorPlanLoaded || !enabled) {
      this.stop();
      return;
    }
    this.run();
  }

  pluginDidRemove() {
    this.stop();
  }

  createHammerManager(svgElement) {
    this.hammer = new Hammer.Manager(svgElement, {
      recognizers: [
        [Hammer.Pinch],
        [
          Hammer.Tap,
          {
            taps: 2,
            interval: 200,
            posThreshold: 50,
            threshold: 10,
            time: 600,
          },
        ],
      ],
    });

    this.hammer.on("pinchstart", this.onPinchStart.bind(this));
    this.hammer.on("pinchmove", this.onPinchMove.bind(this));
    this.hammer.on("pinchend", this.onPinchEnd.bind(this));
    this.hammer.on("tap", this.onDoubletap.bind(this));
  }

  run() {
    const { svgElement } = this.props.triFloorPlan;
    if (this.hammer != null && this.hammer.element !== svgElement) {
      this.hammer.element.removeEventListener("wheel", this.onWheelListener);
      this.hammer.destroy();
      this.hammer = null;
    }

    if (this.hammer === null) {
      this.createHammerManager(svgElement);
      svgElement.addEventListener("wheel", this.onWheelListener);
    }
  }

  stop() {
    if (this.hammer !== null) {
      this.hammer.element.removeEventListener("wheel", this.onWheelListener);
      this.hammer.destroy();
      this.hammer = null;
    }
  }

  onWheelListener = (e) => {
    const { triFloorPlan } = this.props;
    e.preventDefault();
    const {
      MAX_MOUSE_WHEEL_SCALE_FACTOR,
      MOUSE_WHEEL_MODIFIER,
    } = TriFloorPlanUtils;

    let wheelScaleFactor = MAX_MOUSE_WHEEL_SCALE_FACTOR;
    if( typeof this.props.wheelScaleFactor === "number" )
    {
      wheelScaleFactor = this.props.wheelScaleFactor;
    }

    let delta = 1 - e.deltaY * MOUSE_WHEEL_MODIFIER[e.deltaMode];
    delta = Math.min(
      Math.max( delta, 1 / wheelScaleFactor ),
      wheelScaleFactor
    );
    this.applyScale(
      triFloorPlan.svgViewBox.scaleX * delta,
      triFloorPlan.svgViewBox.scaleY * delta,
      {
        x: e.clientX,
        y: e.clientY,
      }
    );
  };

  onPinchStart(e) {
    const { svgViewBox } = this.props.triFloorPlan;
    this.pinchInfo = {
      startScaleX: svgViewBox.scaleX,
      startScaleY: svgViewBox.scaleY,
    };
    this.applyScale(
      this.pinchInfo.startScaleX * e.scale,
      this.pinchInfo.startScaleY * e.scale,
      e.center
    );
  }

  onPinchMove(e) {
    if (this.pinchInfo === null) return;
    this.applyScale(
      this.pinchInfo.startScaleX * e.scale,
      this.pinchInfo.startScaleY * e.scale,
      e.center
    );
  }

  onPinchEnd() {
    this.pinchInfo = null;
  }

  onDoubletap(e) {
    const { svgViewBox } = this.props.triFloorPlan;
    const { center } = e;
    setTimeout(() => {
      this.applyScale(
        svgViewBox.scaleX * this.doubleTapZoomFactor,
        svgViewBox.scaleY * this.doubleTapZoomFactor,
        center
      );
    });
  }

  applyScale = throttle((newScaleX, newScaleY, center) => {
    const { triFloorPlan, floorPlanLoaded } = this.props;
    if (!floorPlanLoaded) {
      return;
    }
    const {
      svgViewBox,
      svgElement,
      initialSvgViewBox,
      svgViewPort,
    } = triFloorPlan;
    triFloorPlan.svgViewBox = TriFloorPlanUtils.scaleSvgViewBox(
      svgViewBox,
      svgElement,
      initialSvgViewBox,
      svgViewPort,
      this.minScale,
      this.maxScale,
      newScaleX,
      newScaleY,
      center
    );
  }, 10);

  zoomTo(scale) {
    const { triFloorPlan, floorPlanLoaded } = this.props;
    if (!floorPlanLoaded) {
      return;
    }
    const { initialSvgViewBox } = triFloorPlan;
    let adjustedScale = scale < this.minScale ? this.minScale : scale;
    adjustedScale =
      adjustedScale > this.maxScale ? this.maxScale : adjustedScale;
    if (adjustedScale > 0) {
      this.applyScale(
        initialSvgViewBox.scaleX * adjustedScale,
        initialSvgViewBox.scaleY * adjustedScale
      );
    }
  }
}
